import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import Pressable from '@atlaskit/primitives/pressable';
import Tooltip from '@atlaskit/tooltip';
import useIconButton from './use-icon-button';
/**
 * __Icon Button__
 *
 * TODO: Description
 *
 * - [Examples](https://atlassian.design/components/icon-button/examples)
 * - [Code](https://atlassian.design/components/icon-button/code)
 * - [Usage](https://atlassian.design/components/icon-button/usage)
 */
const IconButton = /*#__PURE__*/React.memo( /*#__PURE__*/React.forwardRef(function Button({
  // Prevent duplicate labels being added.
  'aria-label': preventedAriaLabel,
  'aria-labelledby': ariaLabelledBy,
  analyticsContext,
  appearance,
  autoFocus,
  icon,
  interactionName,
  isDisabled,
  isLoading,
  isSelected,
  isTooltipDisabled = true,
  label,
  onClick,
  onClickCapture,
  onKeyDownCapture,
  onKeyUpCapture,
  onMouseDownCapture,
  onMouseUpCapture,
  onPointerDownCapture,
  onPointerUpCapture,
  onTouchEndCapture,
  onTouchStartCapture,
  shape,
  spacing,
  testId,
  tooltip,
  type = 'button',
  UNSAFE_size,
  ...unsafeRest
}, ref) {
  // @ts-expect-error
  const {
    className: _className,
    css: _css,
    as: _as,
    style: _style,
    ...saferRest
  } = unsafeRest;
  const rest = getBooleanFF('platform.design-system-team.remove-unsafe-spread-from-new-button_a2xhw') ? saferRest : unsafeRest;

  /**
   * TODO: At some stage I'll look into re-using more logic across 'default' and 'icon'
   * buttons. It's currently duplicated and mostly the same.
   */
  const baseProps = useIconButton({
    analyticsContext,
    appearance,
    ariaLabelledBy,
    autoFocus,
    buttonType: 'button',
    icon,
    interactionName,
    isDisabled,
    isLoading,
    isSelected,
    label,
    onClick,
    onClickCapture,
    onKeyDownCapture,
    onKeyUpCapture,
    onMouseDownCapture,
    onMouseUpCapture,
    onPointerDownCapture,
    onPointerUpCapture,
    onTouchEndCapture,
    onTouchStartCapture,
    ref,
    shape,
    spacing,
    testId,
    UNSAFE_size
  });
  if (!isTooltipDisabled) {
    var _tooltip$content;
    return /*#__PURE__*/React.createElement(Tooltip, {
      content: (_tooltip$content = tooltip === null || tooltip === void 0 ? void 0 : tooltip.content) !== null && _tooltip$content !== void 0 ? _tooltip$content : label,
      testId: tooltip === null || tooltip === void 0 ? void 0 : tooltip.testId,
      position: tooltip === null || tooltip === void 0 ? void 0 : tooltip.position,
      delay: tooltip === null || tooltip === void 0 ? void 0 : tooltip.delay,
      onShow: tooltip === null || tooltip === void 0 ? void 0 : tooltip.onShow,
      onHide: tooltip === null || tooltip === void 0 ? void 0 : tooltip.onHide,
      mousePosition: tooltip === null || tooltip === void 0 ? void 0 : tooltip.mousePosition,
      analyticsContext: tooltip === null || tooltip === void 0 ? void 0 : tooltip.analyticsContext,
      strategy: tooltip === null || tooltip === void 0 ? void 0 : tooltip.strategy,
      tag: tooltip === null || tooltip === void 0 ? void 0 : tooltip.tag,
      truncate: tooltip === null || tooltip === void 0 ? void 0 : tooltip.truncate,
      component: tooltip === null || tooltip === void 0 ? void 0 : tooltip.component,
      hideTooltipOnClick: tooltip === null || tooltip === void 0 ? void 0 : tooltip.hideTooltipOnClick,
      hideTooltipOnMouseDown: tooltip === null || tooltip === void 0 ? void 0 : tooltip.hideTooltipOnMouseDown
    }, triggerProps => /*#__PURE__*/React.createElement(Pressable, _extends({}, rest, {
      // Top level props
      "aria-labelledby": baseProps['aria-labelledby'],
      type: type,
      testId: testId,
      componentName: "IconButton",
      analyticsContext: analyticsContext,
      interactionName: interactionName
      // Shared between tooltip and native props
      ,
      onMouseOver: e => {
        var _triggerProps$onMouse, _rest$onMouseOver;
        (_triggerProps$onMouse = triggerProps.onMouseOver) === null || _triggerProps$onMouse === void 0 ? void 0 : _triggerProps$onMouse.call(triggerProps, e);
        (_rest$onMouseOver = rest.onMouseOver) === null || _rest$onMouseOver === void 0 ? void 0 : _rest$onMouseOver.call(rest, e);
      },
      onMouseOut: e => {
        var _triggerProps$onMouse2, _rest$onMouseOut;
        (_triggerProps$onMouse2 = triggerProps.onMouseOut) === null || _triggerProps$onMouse2 === void 0 ? void 0 : _triggerProps$onMouse2.call(triggerProps, e);
        (_rest$onMouseOut = rest.onMouseOut) === null || _rest$onMouseOut === void 0 ? void 0 : _rest$onMouseOut.call(rest, e);
      },
      onMouseMove: e => {
        var _triggerProps$onMouse3, _rest$onMouseMove;
        (_triggerProps$onMouse3 = triggerProps.onMouseMove) === null || _triggerProps$onMouse3 === void 0 ? void 0 : _triggerProps$onMouse3.call(triggerProps, e);
        (_rest$onMouseMove = rest.onMouseMove) === null || _rest$onMouseMove === void 0 ? void 0 : _rest$onMouseMove.call(rest, e);
      },
      onMouseDown: e => {
        var _triggerProps$onMouse4, _rest$onMouseDown;
        (_triggerProps$onMouse4 = triggerProps.onMouseDown) === null || _triggerProps$onMouse4 === void 0 ? void 0 : _triggerProps$onMouse4.call(triggerProps, e);
        (_rest$onMouseDown = rest.onMouseDown) === null || _rest$onMouseDown === void 0 ? void 0 : _rest$onMouseDown.call(rest, e);
      },
      onFocus: e => {
        var _triggerProps$onFocus, _rest$onFocus;
        (_triggerProps$onFocus = triggerProps.onFocus) === null || _triggerProps$onFocus === void 0 ? void 0 : _triggerProps$onFocus.call(triggerProps, e);
        (_rest$onFocus = rest.onFocus) === null || _rest$onFocus === void 0 ? void 0 : _rest$onFocus.call(rest, e);
      },
      onBlur: e => {
        var _triggerProps$onBlur, _rest$onBlur;
        (_triggerProps$onBlur = triggerProps.onBlur) === null || _triggerProps$onBlur === void 0 ? void 0 : _triggerProps$onBlur.call(triggerProps, e);
        (_rest$onBlur = rest.onBlur) === null || _rest$onBlur === void 0 ? void 0 : _rest$onBlur.call(rest, e);
      }
      // Shared between tooltip and base props
      ,
      onClick: (event, analyticsEvent) => {
        var _baseProps$onClick, _triggerProps$onClick;
        baseProps === null || baseProps === void 0 ? void 0 : (_baseProps$onClick = baseProps.onClick) === null || _baseProps$onClick === void 0 ? void 0 : _baseProps$onClick.call(baseProps, event, analyticsEvent);
        triggerProps === null || triggerProps === void 0 ? void 0 : (_triggerProps$onClick = triggerProps.onClick) === null || _triggerProps$onClick === void 0 ? void 0 : _triggerProps$onClick.call(triggerProps, event);
      },
      ref: ref => {
        var _triggerProps$ref;
        baseProps.ref(ref);
        triggerProps === null || triggerProps === void 0 ? void 0 : (_triggerProps$ref = triggerProps.ref) === null || _triggerProps$ref === void 0 ? void 0 : _triggerProps$ref.call(triggerProps, ref);
      }
      // Base props only
      ,
      xcss: baseProps.xcss,
      isDisabled: baseProps.isDisabled,
      onMouseDownCapture: baseProps.onMouseDownCapture,
      onMouseUpCapture: baseProps.onMouseUpCapture,
      onKeyDownCapture: baseProps.onKeyDownCapture,
      onKeyUpCapture: baseProps.onKeyUpCapture,
      onTouchStartCapture: baseProps.onTouchStartCapture,
      onTouchEndCapture: baseProps.onTouchEndCapture,
      onPointerDownCapture: baseProps.onPointerDownCapture,
      onPointerUpCapture: baseProps.onPointerUpCapture,
      onClickCapture: baseProps.onClickCapture
    }), baseProps.children));
  }
  return /*#__PURE__*/React.createElement(Pressable, _extends({}, rest, {
    "aria-labelledby": baseProps['aria-labelledby'],
    ref: baseProps.ref,
    xcss: baseProps.xcss,
    isDisabled: baseProps.isDisabled,
    onClick: baseProps.onClick,
    onMouseDownCapture: baseProps.onMouseDownCapture,
    onMouseUpCapture: baseProps.onMouseUpCapture,
    onKeyDownCapture: baseProps.onKeyDownCapture,
    onKeyUpCapture: baseProps.onKeyUpCapture,
    onTouchStartCapture: baseProps.onTouchStartCapture,
    onTouchEndCapture: baseProps.onTouchEndCapture,
    onPointerDownCapture: baseProps.onPointerDownCapture,
    onPointerUpCapture: baseProps.onPointerUpCapture,
    onClickCapture: baseProps.onClickCapture,
    type: type,
    testId: testId,
    componentName: "IconButton",
    analyticsContext: analyticsContext,
    interactionName: interactionName
  }), baseProps.children);
}));
IconButton.displayName = 'IconButton';
export default IconButton;